'use client';

import { Box, Button, Grid, Paper, Stack, Typography, useTheme } from '@mui/material';

import { formatCurrencyRange } from '@/lib/helpers/formatHelpers';
import { ExtendedLenderTier } from '@/lib/loanFilteringHelper';

type LoanRowProps = {
  tier: ExtendedLenderTier;
};

function LoanStats({
  label,
  value,
  sub,
  minWidth = 160,
  maxWidth = 200,
}: {
  label: string;
  value: string;
  sub?: string;
  minWidth?: number | string;
  maxWidth?: number | string;
}) {
  return (
    <Box
      sx={{
        minWidth: { xs: '100%', sm: minWidth },
        maxWidth: { xs: '100%', sm: maxWidth },
        flex: { xs: '1 1 100%', sm: '1 0 auto' },
        textAlign: { xs: 'center', sm: 'left' },
      }}
    >
      <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.4, mb: 0.5 }}>
        {label}
      </Typography>

      <Typography variant="h6" fontWeight="700" sx={{ lineHeight: 1.2, color: 'text.primary' }}>
        {value}
      </Typography>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{
          lineHeight: 1.4,
          minHeight: '1.25rem',
          mb: 0.5,
        }}
      >
        {sub || '\u00A0'}
      </Typography>
    </Box>
  );
}

function formatRange(
  min: number | null | undefined,
  max: number | null | undefined,
  type: 'currency' | 'percent' | 'months'
): string | null {
  const isInvalid = (val: any) => val === null || val === undefined || isNaN(val);

  if (isInvalid(min) && isInvalid(max)) return null;

  min = min || 0;
  max = max || 0;

  if (type === 'currency') {
    return formatCurrencyRange(min, max);
  }

  if (type === 'percent') {
    if (min === 0 && max === 0) return null;
    if (min === max) return `${min.toFixed(2)}%`;
    return `${min.toFixed(2)}% – ${max.toFixed(2)}%`;
  }

  if (type === 'months') {
    if (max > 1200 || max === 99999999 || (min === 0 && max === 0)) return null;
    const minYears = Math.floor(min / 12);
    const maxYears = Math.floor(max / 12);
    if (minYears === maxYears) return `${minYears} years`;
    return `${minYears} – ${maxYears} years`;
  }

  return null;
}

export default function LoanRow({ tier }: LoanRowProps) {
  const theme = useTheme();
  const interestRange = tier.hideApr ? null : formatRange(tier.minApr, tier.maxApr, 'percent');

  return (
    <Paper
      variant="outlined"
      sx={{
        p: { xs: 2, sm: 3 },
        borderRadius: 3,
        background: theme.palette.common.white,
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Grid
        container
        spacing={2}
        alignItems="center"
        justifyContent="space-between"
        direction={{ xs: 'column', sm: 'row' }}
      >
        {/* Logo */}
        <Grid item>
          <Box
            component="img"
            src={tier.lenderLogo ?? ''}
            alt={tier.lenderName ?? ''}
            sx={{
              height: 80,
              maxWidth: 100,
              minWidth: 100,
              objectFit: 'contain',
              display: 'block',
              mx: { xs: 'auto', sm: 0 },
            }}
          />
        </Grid>

        {/* Middle details */}
        <Grid item xs>
          <Box
            sx={{
              textAlign: { xs: 'center', sm: 'left' },
              mt: { xs: 1, sm: 0 },
            }}
          >
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              {tier.lenderName} {tier.tierName && ` ${tier.tierName}`}
            </Typography>

            <Stack
              direction="row"
              spacing={4}
              flexWrap="wrap"
              justifyContent={{ xs: 'center', sm: 'flex-start' }}
              useFlexGap
            >
              <LoanStats
                label="Interest rates from"
                value={interestRange || 'Quoted on application'}
                sub={interestRange ? 'per annum' : undefined}
                minWidth={200}
              />
              <LoanStats
                label="Loan amounts"
                value={
                  formatRange(tier.minLoanAmount, tier.maxLoanAmount, 'currency') ||
                  'Quoted on application'
                }
                minWidth={180}
              />
              <LoanStats
                label="Loan terms"
                value={
                  formatRange(tier.minLoanTerm, tier.maxLoanTerm, 'months') ||
                  'Quoted on application'
                }
                minWidth={160}
              />
            </Stack>
          </Box>
        </Grid>

        <Grid
          item
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            mt: { xs: 2, sm: 0 },
          }}
        >
          <Button
            href={process.env.NEXT_PUBLIC_FULL_APP_URL || process.env.FULL_APP_URL || '#'}
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              borderRadius: 999,
              fontWeight: 600,
              fontSize: '1.125rem',
              px: { xs: 4, sm: 5 },
              py: 1.5,
              mb: 1,
              width: '100%',
              maxWidth: 200,
              backgroundColor: theme.palette.brand.primary,
              color: 'white',
              textAlign: 'center',
              '&:hover': {
                backgroundColor: theme.palette.brand.primaryDark || theme.palette.brand.primary,
              },
            }}
          >
            Compare now
          </Button>
          <Typography variant="label1" sx={{ opacity: 0.85 }}>
            on Lend.com.au
          </Typography>
        </Grid>
      </Grid>
    </Paper>
  );
}
